import { useCallback, useState, useRef } from 'react';
import { RHYTHM_CONFIG } from '../config/RhythmConfig';

export const useRhythmJudgment = ({
  currentTimeRef,
  showResult,
  baseScore,
  hitNotesRef,
  missedNotesRef,
  comboRef,
  setCombo,
  setScore,
  setMaxCombo,
  perfectGroupsRef,
  groupTouchedLanesRef,
  groupTimeoutsRef,
  laneQueuesRef,
  activeTouchesRef,
  scoreRef,
  maxComboRef,
  PERFECT_WINDOW,
  GOOD_WINDOW,
  MISS_WINDOW,
  currentBeatmap,
  maxScoreForRound,
  groupCount,
  setShowCombo,
  rhythmConfig = RHYTHM_CONFIG
}) => {
  const [judgeMessage, setJudgeMessage] = useState('');
  const [judgeKey, setJudgeKey] = useState(0);
  const judgeTimeout = useRef(null);
  const startPopAnimationRef = useRef(null); // startPopAnimation을 ref로 관리

  // startPopAnimation 설정 함수
  const setStartPopAnimation = useCallback((func) => {
    startPopAnimationRef.current = func;
  }, []);

  // 판정 메시지
  const lastNoteTime = currentBeatmap[currentBeatmap.length - 1].time;
  const showJudge = useCallback((msg, note, delta) => {
    setJudgeMessage(msg);
    setJudgeKey(prev => prev + 1);
    // 판정메세지가 나타날 때 콤보를 보여줌
    setShowCombo(true);
    if (judgeTimeout.current) clearTimeout(judgeTimeout.current);
    judgeTimeout.current = setTimeout(() => {
      setJudgeMessage(current => (current === msg ? '' : current));
      // 판정메세지가 사라질 때 콤보도 함께 사라지도록 설정
      setShowCombo(false);
    }, rhythmConfig.JUDGE_MESSAGE_DURATION);
  }, []);

  /**
   * 스트럼 스타일 그룹 판정 함수
   * @param {number} key     - 그룹 키(동일 note.time)
   * @param {Array} group    - groupedNotesRef.current[key]
   */
  const evaluateGroup = useCallback((key, group) => {
    let finalResult = 'Perfect';
    let totalScore = 0;
    const currentTime = currentTimeRef.current() * 1000;
    const pressed = groupTouchedLanesRef.current[key] || new Set();
    
    // 마지막 노트인지 확인
    const lastNoteTime = currentBeatmap[currentBeatmap.length - 1].time;
    const isLastNote = group.some(note => note.time === lastNoteTime);

    // 조건 체크: 눌린 레인 수와 노트 수 검증
    const allActiveTouches = new Set();
    activeTouchesRef.current.forEach((laneIndex, eventKey) => {
      if (typeof laneIndex === 'number') {
        allActiveTouches.add(laneIndex);
      }
    });

    const pressedLaneCount = allActiveTouches.size;
    const noteCount = group.length;

    // 조건 1: 동시에 눌린 레인이 3개 이상이면 무조건 Miss
    if (pressedLaneCount >= 3) {
      group.forEach(note => {
        const id = `${note.time}-${note.lane}`;
        missedNotesRef.current.add(id);
      });
      finalResult = 'Miss';
      comboRef.current = 0;
      setCombo(0);
      showJudge('Miss', group[0]);

      if (groupTimeoutsRef.current[key]) {
        clearTimeout(groupTimeoutsRef.current[key]);
        delete groupTimeoutsRef.current[key];
      }
      delete groupTouchedLanesRef.current[key];

      group.forEach(note => {
        const q = laneQueuesRef.current[note.lane];
        if (q?.[0] === note.time) q.shift();
      });
      return;
    }

    // 조건 2: 노트가 1개인데 레인을 2개 이상 눌렀으면 Miss
    if (noteCount === 1 && pressedLaneCount >= 2) {
      group.forEach(note => {
        const id = `${note.time}-${note.lane}`;
        missedNotesRef.current.add(id);
      });
      finalResult = 'Miss';
      comboRef.current = 0;
      setCombo(0);
      showJudge('Miss', group[0]);

      if (groupTimeoutsRef.current[key]) {
        clearTimeout(groupTimeoutsRef.current[key]);
        delete groupTimeoutsRef.current[key];
      }
      delete groupTouchedLanesRef.current[key];

      group.forEach(note => {
        const q = laneQueuesRef.current[note.lane];
        if (q?.[0] === note.time) q.shift();
      });
      return;
    }

    // 조건 3: 노트 수와 눌린 레인 수가 일치하지 않으면 Miss
    if (pressedLaneCount !== noteCount) {
      group.forEach(note => {
        const id = `${note.time}-${note.lane}`;
        missedNotesRef.current.add(id);
      });
      finalResult = 'Miss';
      comboRef.current = 0;
      setCombo(0);
      showJudge('Miss', group[0]);

      if (groupTimeoutsRef.current[key]) {
        clearTimeout(groupTimeoutsRef.current[key]);
        delete groupTimeoutsRef.current[key];
      }
      delete groupTouchedLanesRef.current[key];

      group.forEach(note => {
        const q = laneQueuesRef.current[note.lane];
        if (q?.[0] === note.time) q.shift();
      });
      return;
    }

    // 조건을 만족하면 정상 판정 진행
    let groupPerfect = true;
    let groupMiss = false;
    let hitNotes = []; // GOOD 또는 PERFECT 판정을 받은 노트들

    group.forEach(note => {
      const id = `${note.time}-${note.lane}`;
      const rawDelta = currentTime - note.time;

      // 비대칭 판정: 판정선 이전은 관대하게, 판정선 이후는 엄격하게
      // 판정선 지난 노트는 시간을 추가해서 더 엄격하게 판정
      let adjustedDelta;
      if (rawDelta < 0) {
        adjustedDelta = Math.abs(rawDelta);
      } else {
        adjustedDelta = Math.abs(rawDelta) + 30;
      }

      if (pressed.has(note.lane)) {
        if (adjustedDelta <= PERFECT_WINDOW) {
          hitNotesRef.current.add(id);
          hitNotes.push(note); // GOOD 또는 PERFECT 판정을 받은 노트 추가
        } else if (adjustedDelta <= GOOD_WINDOW) {
          hitNotesRef.current.add(id);
          hitNotes.push(note); // GOOD 또는 PERFECT 판정을 받은 노트 추가
          groupPerfect = false;
          finalResult = finalResult === 'Perfect' ? 'Good' : finalResult;
        } else {
          missedNotesRef.current.add(id);
          groupMiss = true;
          finalResult = 'Miss';
        }
      } else {
        missedNotesRef.current.add(id);
        groupMiss = true;
        finalResult = 'Miss';
      }
    });

    // 그룹 전체가 Perfect일 때만 콤보 증가 및 점수 계산
    if (groupPerfect && !groupMiss) {
      // 마지막 노트가 Perfect이고 모든 그룹이 Perfect였을 때만 만점으로 처리
      if (isLastNote) {
        // 현재 그룹까지 포함해서 모든 그룹이 Perfect인지 확인
        // perfectGroupsRef는 현재 그룹이 Perfect로 판정된 후에 증가되므로, 
        // 현재 시점에서는 이전 그룹들의 Perfect 개수
        if (perfectGroupsRef.current === groupCount - 1) {
          const currentScore = scoreRef.current;
          const remainingScore = maxScoreForRound - currentScore;
          totalScore = remainingScore;
        } else {
          totalScore = Math.floor(baseScore * (1 + comboRef.current / 100));
        }
      } else {
        totalScore = Math.floor(baseScore * (1 + comboRef.current / 100));
      }
    } else if (!groupMiss) {
      totalScore = Math.floor((baseScore * 0.5) * (1 + comboRef.current / 100));
    } else {
      totalScore = 0;
    }

    const deltas = group.map(note => {
      const rawDelta = currentTime - note.time;
      if (rawDelta < 0) {
        return Math.abs(rawDelta);
      } else {
        return Math.abs(rawDelta) + 30;
      }
    });
    const worstDelta = Math.max(...deltas);

    // 콤보·점수·판정 반영
    if (groupMiss) {
      comboRef.current = 0;
      setCombo(0);
      showJudge('Miss', group[0], worstDelta);
    } else {
      setScore(s => {
        const newScore = s + totalScore;
        scoreRef.current = newScore;
        return scoreRef.current;
      });

      const nc = comboRef.current + 1;
      comboRef.current = nc;
      setCombo(nc);
      setMaxCombo(m => {
        maxComboRef.current = Math.max(m, nc);
        return maxComboRef.current;
      });

      if (groupPerfect) {
        perfectGroupsRef.current += 1;
        showJudge('Perfect', group[0], worstDelta);
      } else {
        showJudge('Good', group[0], worstDelta);
      }

      // GOOD 또는 PERFECT 판정을 받은 노트들에 대해 풍선 터지는 효과 시작
      if (startPopAnimationRef.current && hitNotes.length > 0) {
        hitNotes.forEach(note => {
          const noteId = `${note.time}-${note.lane}`;
          startPopAnimationRef.current(noteId, note);
        });
      }
    }

    // 타임아웃·터치 기록 정리
    if (groupTimeoutsRef.current[key]) {
      clearTimeout(groupTimeoutsRef.current[key]);
      delete groupTimeoutsRef.current[key];
    }
    delete groupTouchedLanesRef.current[key];

    // 레인별 큐에서 이 time 제거
    group.forEach(note => {
      const q = laneQueuesRef.current[note.lane];
      if (q?.[0] === note.time) q.shift();
    });
  }, [showResult, baseScore]);

  return {
    judgeMessage,
    setJudgeMessage,
    judgeKey,
    setJudgeKey,
    judgeTimeout,
    showJudge,
    evaluateGroup,
    setStartPopAnimation // 새로 추가된 함수를 반환
  };
};
